import { useCallback } from 'react';
import {
  ballotContext, extensionRules, flowSnapshot, redactor, resolveMap, type Ballot, type BallotFields, type ChatMessage, type FlowCell, type MapArgument, type Round,
} from '@flow/core';
import { run, useDeps, useUseCases } from '../../app/services';
import { apiFetch } from '../../infra/api';
import { useAccount } from '../account/AccountProvider';

const loadFlow = async (deps: ReturnType<typeof useDeps>, round: Round) => {
  const [rows, cellList, names] = await Promise.all([deps.flow.rows(round.id), deps.flow.cells(round.id), deps.names.get(round.id)]);
  return { names, snapshot: flowSnapshot(round, rows, new Map(cellList.map((c): [string, FlowCell] => [c.id, c])), names) };
};

/** Decide, step 1: ask for a map of every argument (made → engaged → extended → weighed) and save it with the ballot. */
export const useMapArguments = () => {
  const { getToken } = useAccount();
  const deps = useDeps();
  const uc = useUseCases();
  return useCallback(async (round: Round): Promise<number> => {
    const { snapshot } = await loadFlow(deps, round);
    if (snapshot.refs.size === 0) return 0;
    const r = await apiFetch<{ map: { arguments: MapArgument[] } }>('/api/ai/map', getToken, {
      method: 'POST',
      body: JSON.stringify({ flow: snapshot.text, rules: extensionRules(round) }),
    });
    const args = resolveMap(r.map.arguments, snapshot.refs);
    await run(uc.saveBallot(round.id, { argumentMap: { createdAt: Date.now(), arguments: args } }));
    return args.length;
  }, [deps, getToken, uc]);
};

export type AssistantMode = 'draft' | 'chat' | 'critique';

/** Changes the assistant made, in the app's terms (real team letters and speaker keys, real names). */
export interface AssistantEdits {
  rfd?: string;
  teamComments?: Partial<Record<'A' | 'B', string>>;
  speakerComments?: Record<string, string>;
}

interface BallotReply {
  reply: string;
  fields: { rfd: string | null; teamComments: { team: string; text: string }[]; speakerComments: { speaker: string; text: string }[] };
}

/**
 * Ballot assistant: draft, follow an instruction, or critique. Each turn sends the form fields as they are right now,
 * so it works from the judge's edits. Names are redacted going out and restored (on the device) coming back; the
 * conversation is stored with tokens, so it respects the name purge too.
 */
export const useBallotAssistant = () => {
  const { getToken } = useAccount();
  const deps = useDeps();
  const uc = useUseCases();
  return useCallback(async (round: Round, ballot: Ballot, fields: BallotFields, mode: AssistantMode, message?: string): Promise<AssistantEdits> => {
    const { names, snapshot } = await loadFlow(deps, round);
    const r = redactor(round, names);
    const history = ballot.assistantChat ?? [];
    const asked: ChatMessage[] = [...history, { role: 'judge', text: r.redact(message?.trim() || (mode === 'draft' ? 'Draft my ballot.' : mode === 'critique' ? 'Critique my ballot.' : '')), at: Date.now() }];
    const out = await apiFetch<BallotReply>('/api/ai/ballot', getToken, {
      method: 'POST',
      body: JSON.stringify({
        mode,
        flow: snapshot.text,
        context: ballotContext(round, ballot, fields, names),
        chat: asked.slice(-20).map(({ role, text }) => ({ role, text })),
      }),
    });

    const keys = (['A', 'B'] as const).flatMap((t) => Array.from({ length: round.format.teamSize }, (_, i) => `${t}${i}`));
    const byToken = new Map(keys.map((k) => [r.speakerToken(k), k]));
    const teamByToken = new Map((['A', 'B'] as const).map((t) => [r.teamToken(t), t]));
    const edits: AssistantEdits = {};
    if (out.fields.rfd !== null) edits.rfd = r.restoreNames(out.fields.rfd);
    for (const t of out.fields.teamComments) {
      const team = teamByToken.get(t.team.trim());
      if (team) (edits.teamComments ??= {})[team] = r.restoreNames(t.text);
    }
    for (const s of out.fields.speakerComments) {
      const key = byToken.get(s.speaker.trim());
      if (key) (edits.speakerComments ??= {})[key] = r.restoreNames(s.text);
    }
    await run(uc.saveBallot(round.id, { assistantChat: [...asked, { role: 'assistant' as const, text: out.reply, at: Date.now() }].slice(-80) }));
    return edits;
  }, [deps, getToken, uc]);
};
